import { useId } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Field, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Label } from "@libs/zapi-ui/ds/components/label";
import { Textarea } from "@libs/zapi-ui/ds/components/textarea";
import { SCORE_LEVEL_LABEL_KEY, scoreLevel, type TRiskInput } from "@/zapi-frontend/shared/types/risks";

type TScoreSliderProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  testId: string;
};

function ScoreSlider({ label, value, onChange, testId }: TScoreSliderProps) {
  const { t } = useTranslation("projectHome");
  const levelLabel = t(SCORE_LEVEL_LABEL_KEY[scoreLevel(value)]);

  return (
    <label className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <FigmaTypography variant="label-2" color="zinc900">
          {levelLabel}
        </FigmaTypography>
      </div>
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full cursor-pointer accent-innoveraZinc-900"
        aria-valuetext={levelLabel}
        data-testid={testId}
      />
      <div className="flex justify-between">
        <FigmaTypography variant="body-3" color="zinc500">
          {t("risks.level.low")}
        </FigmaTypography>
        <FigmaTypography variant="body-3" color="zinc500">
          {t("risks.level.high")}
        </FigmaTypography>
      </div>
    </label>
  );
}

export const isRiskInputComplete = (input: TRiskInput) =>
  Boolean(input.title.trim() && input.whatMustHoldTrue.trim() && input.whyItMatters.trim());

export const toRiskInput = (risk?: TRiskInput | null): TRiskInput => ({
  title: risk?.title ?? "",
  whatMustHoldTrue: risk?.whatMustHoldTrue ?? "",
  whyItMatters: risk?.whyItMatters ?? "",
  impact: risk?.impact ?? 5,
  uncertainty: risk?.uncertainty ?? 5
});

type TRiskFieldsProps = {
  value: TRiskInput;
  onChange: (value: TRiskInput) => void;
};

export function RiskFields({ value, onChange }: TRiskFieldsProps) {
  const { t } = useTranslation("projectHome");
  const titleId = useId();
  const whatMustHoldTrueId = useId();
  const whyItMattersId = useId();
  const set = <K extends keyof TRiskInput>(key: K, fieldValue: TRiskInput[K]) =>
    onChange({ ...value, [key]: fieldValue });

  return (
    <>
      <Field className="w-full gap-2">
        <FieldLabel htmlFor={titleId}>{t("risks.fieldTitle")}</FieldLabel>
        <Input
          id={titleId}
          value={value.title}
          onChange={(event) => set("title", event.target.value)}
          placeholder={t("risks.fieldTitlePlaceholder")}
        />
      </Field>

      <Field className="w-full gap-2">
        <FieldLabel htmlFor={whatMustHoldTrueId}>{t("risks.fieldWhatMustHoldTrue")}</FieldLabel>
        <Textarea
          id={whatMustHoldTrueId}
          className="h-[120px] resize-none"
          value={value.whatMustHoldTrue}
          onChange={(event) => set("whatMustHoldTrue", event.target.value)}
        />
      </Field>

      <Field className="w-full gap-2">
        <FieldLabel htmlFor={whyItMattersId}>{t("risks.fieldWhyItMatters")}</FieldLabel>
        <Textarea
          id={whyItMattersId}
          className="h-[120px] resize-none"
          value={value.whyItMatters}
          onChange={(event) => set("whyItMatters", event.target.value)}
        />
      </Field>

      <ScoreSlider
        label={t("risks.fieldImpact")}
        value={value.impact}
        onChange={(impact) => set("impact", impact)}
        testId="risk-impact-slider"
      />
      <ScoreSlider
        label={t("risks.fieldUncertainty")}
        value={value.uncertainty}
        onChange={(uncertainty) => set("uncertainty", uncertainty)}
        testId="risk-uncertainty-slider"
      />
    </>
  );
}
